import type { FC, ReactNode } from "react";
import { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { HugeiconsIcon } from "@hugeicons/react";
import { Cancel01FreeIcons, PrinterFreeIcons } from "@hugeicons/core-free-icons";

import { Button } from "@/components/Button.tsx";
import { printTextDocument } from "@/utils/printTextDocument.ts";

interface ContentOverlayProps {
	open: boolean;
	onClose: () => void;
	title: string;

	/** Localized "Drucken" label. Omit to hide the print button. */
	printLabel?: string;

	/** Localized accessible name for the icon-only close button. */
	closeLabel: string;

	/** Blocks printing while the body is still loading — a printed skeleton is a blank page. */
	printDisabled?: boolean;
	children: ReactNode;
}

/**
 * Shared overlay shell for read-only legal texts, ported from
 * C:\dev\gen\src\components\ContentOverlay.tsx. Title in the header, an optional print button
 * and the X in the top right, a scrollable body, and a backdrop that closes on click.
 *
 * Printing lives here rather than in the caller because this component owns the scrollable
 * body it has to print; callers only supply the translated label.
 *
 * gen's guided-tour suspend and its nesting-depth z-index hook are dropped: neither driver.js
 * nor a second stacking layer exists in this app, so the backdrop can just claim z-50.
 */
export const ContentOverlay: FC<ContentOverlayProps> = ({
	open,
	onClose,
	title,
	printLabel,
	closeLabel,
	printDisabled,
	children
}) => {
	const panelRef = useRef<HTMLDivElement>(null);
	const bodyRef = useRef<HTMLDivElement>(null);
	const titleId = useId();

	/*
	 * Held in a ref so the effect below can depend on `open` alone. The registration form
	 * re-renders on every keystroke, so a caller's inline arrow would be a new function each
	 * time and would otherwise tear down and rebuild the focus trap continuously.
	 */
	const onCloseRef = useRef(onClose);

	useEffect(() => {
		onCloseRef.current = onClose;
	}, [onClose]);

	useEffect(() => {
		if (!open) {
			return;
		}

		const previouslyFocused = document.activeElement as HTMLElement | null;
		const appRoot = document.getElementById("root");
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				onCloseRef.current();
			}
		};

		document.addEventListener("keydown", handleKeyDown);
		document.body.style.overflow = "hidden";

		// The portal target is document.body, so marking #root inert both traps Tab inside the
		// panel and hides the page behind it from assistive tech, in one attribute.
		appRoot?.setAttribute("inert", "");
		panelRef.current?.focus();

		return () => {
			document.removeEventListener("keydown", handleKeyDown);
			document.body.style.overflow = "";

			// Order matters: while #root is still inert the trigger cannot take focus, and the
			// restore below would silently fall through to <body>.
			appRoot?.removeAttribute("inert");
			previouslyFocused?.focus();
		};
	}, [open]);

	if (!open) {
		return null;
	}

	return createPortal(
		<div
			role="presentation"
			className="bg-brand-dark/30 fixed inset-0 z-50 flex items-center justify-center p-6 backdrop-blur-[2px]"
			onClick={(e) => {
				e.stopPropagation();

				if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
					onCloseRef.current();
				}
			}}
		>
			<div
				ref={panelRef}
				role="dialog"
				aria-modal="true"
				aria-labelledby={titleId}
				tabIndex={-1}
				className={
					"bg-brand-light flex max-h-full w-full max-w-[760px] flex-col overflow-clip rounded-2xl outline-none " +
					"shadow-[0_8px_28px_-6px_rgba(74,68,100,0.10),0_18px_88px_-4px_rgba(74,68,100,0.12)]"
				}
			>
				<div className="border-brand-dark/10 flex shrink-0 items-start justify-between gap-4 border-b px-6 py-4">
					<h2 id={titleId} className="text-brand-dark-900 text-lg font-semibold tracking-[-0.26px]">
						{title}
					</h2>
					<div className="flex items-center gap-2">
						{printLabel && (
							<Button
								type="button"
								variant="outline"
								size="sm"
								disabled={printDisabled}
								onClick={() => {
									// Synchronous, so the popup blocker treats the window as user-opened.
									if (bodyRef.current) {
										printTextDocument(bodyRef.current, title);
									}
								}}
							>
								<HugeiconsIcon icon={PrinterFreeIcons} size={16} className="shrink-0" />
								{printLabel}
							</Button>
						)}
						<Button
							type="button"
							variant="outline"
							iconOnly
							aria-label={closeLabel}
							onClick={() => onCloseRef.current()}
						>
							<HugeiconsIcon icon={Cancel01FreeIcons} size={20} className="text-brand-dark/70" />
						</Button>
					</div>
				</div>
				<div
					ref={bodyRef}
					className="text-brand-dark/70 min-h-0 flex-1 overflow-y-auto px-6 py-5 text-sm leading-relaxed"
				>
					{children}
				</div>
			</div>
		</div>,
		document.body
	);
};
